import React, { useState, useEffect, useMemo } from 'react';
import { IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent, IonList, IonItem, IonLabel, IonText, IonIcon, IonSearchbar, IonSpinner } from '@ionic/react';
import { openOutline, searchOutline } from 'ionicons/icons';
import { connect } from '../data/connect';
import { setShowEventsModal } from '../data/eventsModal/events.actions';
import {
  setIsLoggedIn,
  setUsername,
  setUserCed,
  setUserID,
  setUserOther,
  setEventCode,
  setEventName,
  setEventStartDate,
  setEventEndDate,
  setEventLocation
} from '../data/user/user.actions';
import { useHistory } from 'react-router';
import { loginWithEvent as loginWithEventService } from '../data/eventsModal/events.service';
import { configService } from '../services/ConfigService';

interface Evento {
  texto2: string;  // Nombre del evento
  texto3?: string; // Fecha y hora de inicio
  texto4?: string; // Fecha y hora de finalización
  texto6?: string; // Ubicación del evento
  eventCode: string;
}

interface EventoCert {
  texto2?: string;  // Nombre del evento
  texto3?: string; // Fecha y hora de inicio
  texto4?: string; // Fecha y hora de finalización
  texto6?: string; // Ubicación del evento
  eventCode: string;
}

interface StateProps {
  showEventsModal: boolean;
  userced?: string;
}

interface DispatchProps {
  setShowEventsModal: typeof setShowEventsModal;
  setIsLoggedIn: typeof setIsLoggedIn;
  setUsername: typeof setUsername;
  setUserCed: typeof setUserCed;
  setUserID: typeof setUserID;
  setUserOther: typeof setUserOther;
  setEventCode: typeof setEventCode;
  setEventName: typeof setEventName;
  setEventStartDate: typeof setEventStartDate;
  setEventEndDate: typeof setEventEndDate;
  setEventLocation: typeof setEventLocation;
}

interface EventsModalProps extends StateProps, DispatchProps {}

const EventsModal: React.FC<EventsModalProps> = ({
  showEventsModal,
  setShowEventsModal,
  userced,
  setIsLoggedIn,
  setUsername,
  setUserCed,
  setUserID,
  setUserOther,
  setEventCode,
  setEventName,
  setEventStartDate,
  setEventEndDate,
  setEventLocation
}) => {
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [eventosCert, setEventosCert] = useState<EventoCert[]>([]);
  const [eventoError, setEventoError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingEvent, setLoadingEvent] = useState<string | null>(null);
  const [loadingCertEvent, setLoadingCertEvent] = useState<string | null>(null);
  const history = useHistory();

  // Cargar eventos cuando se abre el modal
  useEffect(() => {
    if (showEventsModal && userced) {
      loadEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEventsModal, userced]);

  const loadEvents = async () => {
    if (!userced) {
      setEventoError("No hay una sesión activa. Por favor, inicie sesión primero.");
      return;
    }

    setLoading(true);
    setEventoError("");

    try {
      // Usar ConfigService para construir el request
      const response = await configService.loginMultiEvent(userced);

      if (!response.ok) {
        throw new Error(`Error de red: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Eventos recibidos:", data.eventos);
      console.log("Eventos certificados recibidos:", data.eventoscert);

      if (data.error) {
        setEventoError("Error al cargar eventos: " + data.error);
      } else {
        // Verificar si hay un evento por defecto configurado para login directo
        const defaultEventID = configService.getDefaultEventID();
        let defaultEventFound = false;

        // Verificamos la propiedad "eventos"
        if (data.eventos && Array.isArray(data.eventos) && data.eventos.length > 0) {
          // Guardamos los eventos tal como vienen de la API
          setEventos(data.eventos);
          setEventoError("");

          // ✅ ESTRATEGIA: Login directo si hay evento por defecto configurado
          if (defaultEventID) {
            console.log("Buscando evento por defecto para login directo:", defaultEventID);

            // Buscar el evento por defecto en la lista
            const defaultEvent = data.eventos.find((evento: any) =>
              evento.eventCode === defaultEventID || evento.texto1 === defaultEventID
            );

            if (defaultEvent) {
              console.log("Evento por defecto encontrado, ejecutando login directo:", defaultEvent);
              defaultEventFound = true;
              // ✅ Login directo SIN mostrar el modal
              loginWithEvent(defaultEvent);
              return; // Salir de la función sin mostrar el modal
            }
          }
        } else {
          setEventos([]);
          setEventoError("No estás registrado en ningún evento");
        }

        // Verificamos si hay eventos certificados adicionales
        if (data.eventoscert && Array.isArray(data.eventoscert) && data.eventoscert.length > 0) {
          // Guardamos los eventos certificados tal como vienen de la API
          setEventosCert(data.eventoscert);

          // También verificar en eventos certificados si no se encontró en eventos normales
          if (defaultEventID && !defaultEventFound) {
            console.log("Buscando evento por defecto en eventos certificados:", defaultEventID);

            const defaultCertEvent = data.eventoscert.find((evento: any) =>
              evento.eventCode === defaultEventID || evento.texto1 === defaultEventID
            );

            if (defaultCertEvent) {
              console.log("Evento por defecto encontrado en certificados, ejecutando login directo:", defaultCertEvent);
              // ✅ Login directo SIN mostrar el modal
              loginWithEvent(defaultCertEvent);
              return; // Salir de la función sin mostrar el modal
            }
          }
        } else {
          setEventosCert([]);
        }

        // Si llegamos aquí, no se encontró evento por defecto o no está configurado
        // Mostrar mensaje si no hay evento por defecto configurado
        if (defaultEventID && !defaultEventFound) {
          console.log("Evento por defecto no encontrado en la lista de eventos disponibles");
        }
      }
    } catch (error) {
      console.error("Error al cargar eventos:", error);
      setEventoError("Error de conexión. Por favor, verifique su conexión a internet e intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // Filtramos los eventos según el término de búsqueda
  const filteredEventos = useMemo(() => {
    if (!searchTerm.trim()) return eventos;

    const searchTermLower = searchTerm.toLowerCase().trim();
    return eventos.filter(evento => {
      // Buscamos en el nombre del evento
      const nameMatch = evento.texto2?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de inicio
      const startDateMatch = evento.texto3?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de finalización
      const endDateMatch = evento.texto4?.toLowerCase().includes(searchTermLower);
      // Buscamos en la ubicación
      const locationMatch = evento.texto6?.toLowerCase().includes(searchTermLower);

      return nameMatch || startDateMatch || endDateMatch || locationMatch;
    });
  }, [eventos, searchTerm]);

  // Filtramos los eventos certificados según el término de búsqueda
  const filteredEventosCert = useMemo(() => {
    if (!searchTerm.trim()) return eventosCert;

    const searchTermLower = searchTerm.toLowerCase().trim();
    return eventosCert.filter(eventoCert => {
      // Buscamos en el nombre del evento
      const nameMatch = eventoCert.texto2?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de inicio
      const startDateMatch = eventoCert.texto3?.toLowerCase().includes(searchTermLower);
      // Buscamos en la fecha de finalización
      const endDateMatch = eventoCert.texto4?.toLowerCase().includes(searchTermLower);
      // Buscamos en la ubicación
      const locationMatch = eventoCert.texto6?.toLowerCase().includes(searchTermLower);

      return nameMatch || startDateMatch || endDateMatch || locationMatch;
    });
  }, [eventosCert, searchTerm]);

  // Función para hacer login con el evento seleccionado
  // Utilizamos la función exportada desde el servicio
  const loginWithEvent = async (evento: Evento) => {
    // Depuración: Mostramos el evento seleccionado
    console.log('Evento seleccionado en EventsModal:', evento);

    // Establecemos el evento que está cargando
    setLoadingEvent(evento.texto2);

    try {
      console.log('Llamando a loginWithEventService con evento:', evento.eventCode);

      // Llamamos a la función del servicio con todos los parámetros necesarios
      await loginWithEventService(
        evento,
        userced,
        setIsLoggedIn,
        setUsername,
        setUserCed,
        setUserID,
        setUserOther,
        setEventCode,
        setEventName,
        setEventStartDate,
        setEventEndDate,
        setEventLocation,
        setShowEventsModal,
        history,
        setLoading,
        setEventoError
      );

      console.log('loginWithEventService completado para evento:', evento.eventCode);
    } catch (error) {
      console.error('Error en loginWithEvent:', error);
      // Limpiamos el estado de carga del evento en caso de error
      setLoadingEvent(null);
    }
    // Nota: No limpiamos el estado de carga aquí porque el servicio se encarga de cerrar el modal
    // y el componente se desmonta, lo que hace que el estado de carga ya no sea relevante
  };

  return (
    <IonModal isOpen={showEventsModal} onDidDismiss={() => setShowEventsModal(false)} className="events-modal">
      <IonHeader>
        <IonToolbar className="modal-toolbar">
          <IonTitle>Eventos</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setShowEventsModal(false)}>Cerrar</IonButton>
          </IonButtons>
        </IonToolbar>
        {!loading && !eventoError && (eventos.length > 0 || eventosCert.length > 0) && (
          <IonToolbar>
            <IonSearchbar
              value={searchTerm}
              onIonChange={(e) => setSearchTerm(e.detail.value || '')}
              placeholder="Buscar por nombre, fecha o ubicación..."
              animated={true}
              debounce={300}
              showCancelButton="never"
              style={{
                '--background': document.body.classList.contains('dark-theme') ? '#1e1e1e' : 'rgba(255, 255, 255, 0.8)',
                '--color': document.body.classList.contains('dark-theme') ? 'white' : 'black',
                '--placeholder-color': document.body.classList.contains('dark-theme') ? 'rgba(255, 255, 255, 0.6)' : undefined,
                'border': document.body.classList.contains('dark-theme') ? '1px solid rgba(255, 255, 255, 0.1)' : undefined,
                'borderRadius': '8px'
              }}
            />
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent className="modal-content">
        {loading ? (
          <div className="ion-padding ion-text-center">
            <p>Cargando eventos...</p>
          </div>
        ) : eventoError ? (
          <div className="error-message ion-padding ion-text-center ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid #ffcdd2', boxShadow: 'var(--shadow-sm)' }}>
            <IonText color="danger">
              <p>{eventoError}</p>
            </IonText>
          </div>
        ) : searchTerm.trim() !== '' && filteredEventos.length === 0 && filteredEventosCert.length === 0 ? (
          <div className="ion-padding ion-text-center ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)' }}>
            <IonText>
              <p>No se encontraron eventos que coincidan con la búsqueda "{searchTerm}".</p>
            </IonText>
          </div>
        ) : (
          <>
            {/* Sección de eventos principales */}
            {filteredEventos.length > 0 && (
              <div className="events-container ion-padding ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)' }}>
                <h2 className="ion-text-center" style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Eventos disponibles:</h2>
                <IonList className="events-list">
                  {filteredEventos.map((evento, index) => (
                    <IonItem
                      key={index}
                      button
                      onClick={() => loadingEvent === null && loginWithEvent(evento)}
                      lines="none"
                      className="event-item"
                      style={{
                        '--background': 'rgba(255, 255, 255, 0.5)',
                        '--color': 'var(--neutral-900)',
                        borderRadius: '8px',
                        border: '1px solid var(--neutral-300)',
                        margin: '8px 0',
                        position: 'relative'
                      }}
                    >
                      {loadingEvent === evento.texto2 ? (
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                            <IonSpinner name="crescent" style={{ color: 'var(--ion-color-primary)' }} />
                            <p style={{ margin: 0, color: 'var(--ion-color-primary)', fontWeight: 'bold' }}>Entrando...</p>
                          </div>
                        </div>
                      ) : (
                        <IonLabel>
                          <h2 style={{ fontWeight: 'bold', fontSize: '16px', whiteSpace: 'normal', overflow: 'visible' }}>
                            {evento.texto2}
                          </h2>
                          {(evento.texto3 || evento.texto4) && (
                            <p style={{ color: 'var(--neutral-700)', fontSize: '14px', marginTop: '4px' }}>
                              <span style={{ display: 'block' }}>
                                <strong>Inicio:</strong> {evento.texto3 || 'No disponible'}
                              </span>
                              <span style={{ display: 'block' }}>
                                <strong>Fin:</strong> {evento.texto4 || 'No disponible'}
                              </span>
                              {evento.texto6 && (
                                <span style={{ display: 'block' }}>
                                  <strong>Ubicación:</strong> {evento.texto6}
                                </span>
                              )}
                            </p>
                          )}
                        </IonLabel>
                      )}
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}

            {/* Sección de otros eventos (eventoscert) */}
            {eventosCert.length > 0 && filteredEventosCert.length > 0 && (
              <div className="other-events-container ion-padding ion-margin" style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--neutral-200)', boxShadow: 'var(--shadow-sm)', marginTop: '20px' }}>
                <h2 className="ion-text-center" style={{ color: 'var(--neutral-900)', fontWeight: '600' }}>Anteriores eventos:</h2>
                <IonList className="events-list">
                  {filteredEventosCert.map((eventoCert, index) => (
                    <IonItem
                      key={`cert-${index}`}
                      button
                      onClick={() => {
                        if (loadingCertEvent === null) {
                          setLoadingCertEvent(eventoCert.texto2 || `Evento ${eventoCert.eventCode}`);
                          setTimeout(() => {
                            window.open(`https://www.esimposio.com/e/${eventoCert.eventCode}/${userced}`, '_blank');
                            setLoadingCertEvent(null);
                          }, 500);
                        }
                      }}
                      lines="none"
                      className="event-item"
                      style={{
                        '--background': 'rgba(255, 255, 255, 0.5)',
                        '--color': 'var(--ion-color-primary)',
                        borderRadius: '8px',
                        border: '1px solid var(--neutral-300)',
                        margin: '8px 0',
                        position: 'relative'
                      }}
                    >
                      {loadingCertEvent === (eventoCert.texto2 || `Evento ${eventoCert.eventCode}`) ? (
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                            <IonSpinner name="crescent" style={{ color: 'var(--ion-color-primary)' }} />
                            <p style={{ margin: 0, color: 'var(--ion-color-primary)', fontWeight: 'bold' }}>Entrando...</p>
                          </div>
                        </div>
                      ) : (
                        <IonLabel>
                          <h2 style={{ fontWeight: 'bold', fontSize: '16px', whiteSpace: 'normal', overflow: 'visible' }}>
                            {eventoCert.texto2 || `Evento ${eventoCert.eventCode}`}
                          </h2>
                          {(eventoCert.texto3 || eventoCert.texto4) && (
                            <p style={{ color: 'var(--neutral-700)', fontSize: '14px', marginTop: '4px' }}>
                              <span style={{ display: 'block' }}>
                                <strong>Inicio:</strong> {eventoCert.texto3 || 'No disponible'}
                              </span>
                              <span style={{ display: 'block' }}>
                                <strong>Fin:</strong> {eventoCert.texto4 || 'No disponible'}
                              </span>
                              {eventoCert.texto6 && (
                                <span style={{ display: 'block' }}>
                                  <strong>Ubicación:</strong> {eventoCert.texto6}
                                </span>
                              )}
                            </p>
                          )}
                        </IonLabel>
                      )}
                      {!loadingCertEvent && <IonIcon icon={openOutline} slot="end" />}
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default connect<{}, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    showEventsModal: state.events.showEventsModal,
    userced: state.user.userced
  }),
  mapDispatchToProps: {
    setShowEventsModal,
    setIsLoggedIn,
    setUsername,
    setUserCed,
    setUserID,
    setUserOther,
    setEventCode,
    setEventName,
    setEventStartDate,
    setEventEndDate,
    setEventLocation
  },
  component: EventsModal
});
